import { NgModule } from '@angular/core';
import { ApolloModule, APOLLO_OPTIONS, Apollo } from 'apollo-angular';
import {
  ApolloClientOptions,
  ApolloLink,
  DefaultOptions,
  InMemoryCache,
  NormalizedCacheObject,
  split,
} from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { HttpLink } from 'apollo-angular/http';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { TokenFacade } from '../../services/token-facade.service';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { visit, print, parse } from 'graphql';
const authUri = '/apt-service/auth/graphql';
const bulletinBoardUri = '/apt-service/bulletin-board/graphql';
const catalogUri = '/apt-service/catalog/graphql';
const categoryUri = '/apt-service/category/graphql';
const contractUri = '/apt-service/contract/graphql';
const contractUploadUri = '/apt-service/contractupload/graphql';
const crossreferenceUri = '/apt-service/crossreference/graphql';
const identityUri = '/apt-service/identity/graphql';
const organizationUri = '/apt-service/organization/graphql';
const productUri = '/apt-service/product/graphql';
const purchaseplanUri = '/apt-service/purchaseplan/graphql';
const rfpUri = '/apt-service/rfp/graphql';
const uploadUri = '/apt-service/upload/graphql';
const uploadRequestUri = '/apt-service/uploadrequest/graphql';
const userUri = '/apt-service/user/graphql';
const offerUri = '/apt-service/offer/graphql';
const offerPricingUploadUri = '/apt-service/offerpricingupload/graphql';
const measurementUri = '/apt-service/measurement/graphql';
const memberRecruitingUri = '/apt-service/member-recruiting/graphql';
const streamUri = 'ws://localhost:8000/apt-service/user/graphql/stream';

export enum ApolloInstances {
  AUTH = 'auth',
  BULLETIN_BOARD = 'bulletinboard',
  CATALOG = 'catalog',
  CATEGORY = 'category',
  CONTRACT = 'contract',
  CONTRACT_UPLOAD = 'contractupload',
  CROSS_REFERENCE = 'crossreference',
  IDENTITY = 'identity',
  ORGANIZATION = 'organization',
  PRODUCT = 'product',
  PURCHASEPLAN = 'purchaseplan',
  RFP = 'rfp',
  UPLOAD = 'upload',
  UPLOAD_REQUEST = 'uploadrequest',
  USER = 'user',
  OFFER = 'offer',
  OFFER_UPLOAD_REQUEST = 'offerpricingupload',
  MEASUREMENT = 'measurement',
  RECRUITING = 'member-recruiting',
  STREAM = 'stream',
}

function createDefaultApollo(httpLink: HttpLink) {
  const http = httpLink.create({
    uri: 'You/Should/Be/Using/.Use/On/Apollo/Before/Calling/Something',
  });
  return {
    link: http,
    cache: new InMemoryCache(),
  };
}

@NgModule({
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createDefaultApollo,
      deps: [HttpLink],
    },
  ],
  imports: [ApolloModule],
})
export class GraphQLModule {
  constructor(apollo: Apollo, httpLink: HttpLink, tokenFacade: TokenFacade) {
    const ApolloClients = [
      { id: ApolloInstances.AUTH, uri: authUri },
      { id: ApolloInstances.BULLETIN_BOARD, uri: bulletinBoardUri },
      { id: ApolloInstances.CATALOG, uri: catalogUri },
      { id: ApolloInstances.CATEGORY, uri: categoryUri },
      { id: ApolloInstances.CONTRACT, uri: contractUri },
      { id: ApolloInstances.CONTRACT_UPLOAD, uri: contractUploadUri },
      { id: ApolloInstances.CROSS_REFERENCE, uri: crossreferenceUri },
      { id: ApolloInstances.IDENTITY, uri: identityUri },
      { id: ApolloInstances.ORGANIZATION, uri: organizationUri },
      { id: ApolloInstances.PRODUCT, uri: productUri },
      { id: ApolloInstances.PURCHASEPLAN, uri: purchaseplanUri },
      { id: ApolloInstances.RFP, uri: rfpUri },
      { id: ApolloInstances.UPLOAD, uri: uploadUri },
      { id: ApolloInstances.UPLOAD_REQUEST, uri: uploadRequestUri },
      { id: ApolloInstances.USER, uri: userUri },
      { id: ApolloInstances.OFFER, uri: offerUri },
      { id: ApolloInstances.OFFER_UPLOAD_REQUEST, uri: offerPricingUploadUri },
      { id: ApolloInstances.MEASUREMENT, uri: measurementUri },
      { id: ApolloInstances.RECRUITING, uri: memberRecruitingUri },
      { id: ApolloInstances.STREAM, uri: streamUri },
    ];

    ApolloClients.forEach(({ id, uri }) =>
      apollo.create(
        this.createNewApollo(httpLink, uri, tokenFacade.getToken()),
        id,
      ),
    );
  }

  createNewApollo = (
    httpLink: HttpLink,
    uri: string,
    authToken: string | null,
  ): ApolloClientOptions<NormalizedCacheObject> => {
    const ws = new GraphQLWsLink(
      createClient({
        url: uri,
        connectionParams: async () => {
          return {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          };
        },
      }),
    );
    const http = ApolloLink.from([httpLink.create({ uri })]);

    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      ws,
      http,
    );

    const cleanQueryLink = new ApolloLink((operation, forward) => {
      const cleanedAST = visit(operation.query, {
        Field(node) {
          if (node.name.value === '__typename') {
            return null;
          }
          return node;
        },
      });
      operation.query = parse(print(cleanedAST));
      return forward(operation);
    });

    const finalLink = ApolloLink.from([
      removeTypenameFromVariables(),
      cleanQueryLink,
      splitLink
    ]);
    const cache = new InMemoryCache();

    const defaultOptions: DefaultOptions = {
      watchQuery: {
        fetchPolicy: 'no-cache',
      },
      query: {
        fetchPolicy: 'no-cache',
      },
    };

    return {
      link: finalLink,
      cache,
      defaultOptions,
    };
  };
}
